import { ApolloProvider, getApolloContext } from "@apollo/react-hooks";
import { Hub } from "@aws-amplify/core";
import ApolloClient from "apollo-client";
import mixpanel from "mixpanel-browser";
import React, { useEffect } from "react";
import {
  atom,
  useRecoilState,
  useRecoilValue,
  useRecoilValueLoadable,
  useSetRecoilState,
} from "recoil";
import { AppContext, AppContextType } from "../AppContext";
import {
  appContextState,
  clientState,
  cognitoUserRefreshState,
} from "../client";
import { useLoadingText } from "../components/Loading";
import { LoginOrTourModal } from "./LoginOrTourModal";

type HubCallback = Parameters<typeof Hub.remove>[1];

const authPromptState = atom({
  key: "AuthPromptNeeded",
  default: false,
});

export const WithAuth: React.FC = ({ children }) => {
  const setCognitoRefresh = useSetRecoilState(cognitoUserRefreshState);

  React.useEffect(() => {
    const hubCallback: HubCallback = (data) => {
      if (data.payload.event === "signOut") {
        // Force a refresh of the token to detect the change
        setCognitoRefresh((count) => count + 1);
        mixpanel.track("AuthenticatedNavigator - Saw signOut");
        return;
      }

      if (data.payload.event === "signIn") {
        // Force a refresh of the token to detect the change
        setCognitoRefresh((count) => count + 1);
        mixpanel.track("AuthenticatedNavigator - Saw signIn");
        return;
      }
    };

    Hub.listen("auth", hubCallback);
    return () => {
      Hub.remove("auth", hubCallback);
    };
  }, []);

  useLoadingText("Loading data...");

  const authState = useHasAuth();
  const [authPromptNeeded, setAuthPromptNeeded] = useRecoilState(
    authPromptState
  );

  useEffect(() => {
    if (authState.hasAuth) {
      setAuthPromptNeeded(false);
    }
  }, [authState.hasAuth]);

  if (authState.hasAuth) {
    return (
      <AppContext.Provider value={authState.appContext}>
        <ApolloProvider client={authState.client}>{children}</ApolloProvider>
      </AppContext.Provider>
    );
  }

  if (authPromptNeeded) {
    return <LoginOrTourModal openRequests={1} />;
  }

  return <>{children}</>;
};

export type HasAuthState =
  | { hasAuth: false; reason: "loading" | "error" | "unauthenticated" }
  | {
      hasAuth: true;
      appContext: AppContextType;
      client: ApolloClient<unknown>;
    };

export const useHasAuth = (): HasAuthState => {
  const appContext = useRecoilValueLoadable(appContextState);
  const client = useRecoilValueLoadable(clientState);

  if (
    appContext.state === "hasValue" &&
    client.state === "hasValue" &&
    appContext.contents &&
    client.contents
  ) {
    return {
      hasAuth: true,
      appContext: appContext.contents,
      client: client.contents,
    };
  } else {
    return {
      hasAuth: false,
      reason:
        appContext.state === "loading" || client.state === "loading"
          ? "loading"
          : appContext.state === "hasError" || client.state === "hasError"
          ? "error"
          : "unauthenticated",
    };
  }
};

export const useRequiredLogin = () => {
  // Wait for the login flow to settle if it's in progress
  useRecoilValue(appContextState);
  useRecoilValue(clientState);

  const context = getApolloContext();
  const contextValue = React.useContext(context);
  const setAuthPromptNeeded = useSetRecoilState(authPromptState);

  let timeout: undefined | ReturnType<typeof setTimeout> = undefined;
  let reject: undefined | ((error: Error) => void) = undefined;
  const loggedIn = new Promise((resolve, _reject) => {
    reject = _reject;

    if (contextValue.client) {
      reject = undefined;
      timeout = undefined;
      resolve();
      return;
    }

    timeout = setTimeout(() => {
      setAuthPromptNeeded(true);
    }, 500);
  });

  useEffect(() => {
    () => {
      if (timeout) clearTimeout(timeout);
      if (reject) reject(new Error("Component unmounted"));
    };
  }, [reject, timeout]);

  if (contextValue.client) return;
  throw loggedIn;
};
